import React, {useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {
    DefaultButton,
    DetailsListLayoutMode,
    IColumn,
    IconButton,
    Modal,
    SelectionMode,
    Separator,
    ShimmeredDetailsList,
    Stack,
    Text
} from "@fluentui/react";
import {ApprovalListUpload, contentStyles, iconButtonStyles} from "./index";
import {useBranding} from "../../hooks/useBranding";
import ApprovalService, {
    IGetApprovalListDetailRequest,
    IGetApprovalListDetailResponse,
    IGetApprovalListMasterResponse
} from "../../services/Approval";
import StatusMessage, {StatusEnum} from "../../share/StatusMessage";
import {decode} from 'html-entities'


type Properties = {
    list: IGetApprovalListMasterResponse | undefined
    documentOnlyAudienceId: string
    onDismissModal: () => void
    showModal: boolean
}

export function ApprovalListDetails({
                                        list,
                                        documentOnlyAudienceId,
                                        onDismissModal,
                                        showModal
                                    }: Properties) {

    const {t,} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()

    const [message, setMessage] = useState("")
    const [showUpload, setShowUpload] = useState(false)
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessage, setStatusMessage] = useState<StatusEnum>(StatusEnum.Info)
    const [hasTimerMessage, setHasTimerMessage] = useState(false)
    const [listDetails, setListDetails] = useState<IGetApprovalListDetailResponse[]>([])
    const [isLoadingData, setIsLoadingData] = useState(true)
    const [isLoadedData, setIsLoadedData] = useState(false)


    /**
     *
     */
    useEffect(() => {
        if (showModal) {
            loadListDetail().then()
        }
    }, [showModal])


    /**
     * On Before dismiss
     */
    const onBeforeDismissEmailPreview = () => {
        onDismissModal()
    }


    const loadListDetail = async () => {

        setMessage("")
        setShowMessage(false)
        setIsLoadingData(true)
        setListDetails([])
        setIsLoadedData(false)

        const request: IGetApprovalListDetailRequest = {
            Mode: 'approvalListMasterId',
            Id: String(list?.Id)
        }

        await ApprovalService.getApprovalListDetail(request)
            .then(data => {
                if (data.Success) {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalListDetailResponse>
                    if (Array.isArray(resultData)) {
                        setListDetails(resultData)
                        setIsLoadedData(true)

                    } else {
                        ShowMessage(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } else {
                    ShowMessage(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch((error) => {
                ShowMessage(t('message.error', {ns: 'common', Error: error.message}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoadingData(false)
            })

    }


    /**
     * Displays a message with a specified status and an optional timer.
     *
     * @param {string} message - The message text to be displayed.
     * @param {StatusEnum} status - The status of the message, represented as an enum.
     * @param {boolean} [hasTimer=false] - Optional flag to determine if the message should have a timer. Defaults to false.
     * @return {void} Does not return a value.
     */
    function ShowMessage(message: string, status: StatusEnum, hasTimer: boolean = false): void {
        setMessage(decode(message))
        setStatusMessage(status)
        setHasTimerMessage(hasTimer)
        setShowMessage(true)
    }


    /**
     * Renders a row or column based on the provided item. If the item is null or an empty string,
     * it displays a styled placeholder text. Otherwise, it displays the item's content with styling.
     *
     * @param {string | null} item - The item to render in the row or column. If null or an empty string, a placeholder text is displayed.
     * @return {Element} A JSX element representing the styled content or placeholder text for the specified item.
     */
    function RenderRowColumn(item: string | null): JSX.Element {
        return (
            <>
                {item === null || item.trim().length === 0 ?

                    <Text variant={"small"}
                          styles={{
                              root: {
                                  padding: "4px 8px",
                                  borderRadius: 8,
                                  fontStyle: 'italic',
                                  color: branding.theme.palette.neutralTertiaryAlt,
                                  backgroundColor: branding.theme.palette.neutralLighterAlt
                              }
                          }}
                          title={t('text.emptyColumn', {ns: 'approval'})}
                    >
                        {t('text.emptyColumn', {ns: 'approval'})}
                    </Text>

                    :
                    <Text
                        styles={{
                            root: {
                                width: '100%',
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }
                        }}
                        variant={"medium"}
                        title={item}
                    >
                        {item}
                    </Text>

                }
            </>
        )
    }

    const listColumns: IColumn[] = [
        {
            key: 'column1',
            name: t('title.columns', {ns: 'approval', Name: "1"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column1)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column2',
            name: t('title.columns', {ns: 'approval', Name: "2"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column2)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column3',
            name: t('title.columns', {ns: 'approval', Name: "3"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column3)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column4',
            name: t('title.columns', {ns: 'approval', Name: "4"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column4)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column5',
            name: t('title.columns', {ns: 'approval', Name: "5"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column5)}
                    </Stack>
                </>
            }
        },
        {
            key: 'column6',
            name: t('title.columns', {ns: 'approval', Name: "6"}),
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalListDetailResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {RenderRowColumn(item.Column6)}
                    </Stack>
                </>
            }
        }
    ]

    /**
     * Callback function to hide the upload interface.
     *
     * This function sets the upload interface to a hidden state. Optionally,
     * if the `update` parameter is set to `true`, it triggers the reloading
     * of the list details by calling the `loadListDetail` function.
     *
     * @param {boolean} [update=false] - Determines whether to refresh the list details
     * when the upload interface is hidden.
     */
    const OnHideUpload = (update: boolean = false) => {
        setShowUpload(false)
        if (update) {
            loadListDetail().then()
        }
    }

    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerListPreview}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"}
                                  title={t('title.listDetails', {ns: 'approval', Name: list?.Name})}>
                                {t('title.listDetails', {ns: 'approval', Name: list?.Name})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={onBeforeDismissEmailPreview}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>


                                {showMessage &&
                                    <>
                                        <Stack styles={{root: {paddingTop: 8}}}>
                                            <StatusMessage status={statusMessage}
                                                           hasTimer={hasTimerMessage}
                                                           truncated={true}
                                                           isMultiline={true}
                                                           dismiss={true}

                                                           setShowMessage={setShowMessage}>
                                                <div>{message}</div>
                                            </StatusMessage>
                                        </Stack>
                                    </>
                                }

                                {/*-- START CONTENT --*/}
                                <Stack grow={1}
                                       styles={{root: {overflow: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">


                                    {listDetails.length === 0 && !isLoadingData ?

                                        <Stack styles={{
                                            root: {
                                                height: '100%',
                                                textAlign: 'center',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }
                                        }}>
                                            <Text variant={'small'} block>
                                                <Trans>
                                                    {t('text.emptyDetailList', {ns: 'approval'})}
                                                </Trans>
                                            </Text>
                                        </Stack>
                                        :
                                        <Stack
                                            styles={{root: {paddingTop: 8, width: 840}}}>


                                            <ShimmeredDetailsList
                                                items={listDetails}
                                                onShouldVirtualize={() => false}
                                                enableShimmer={isLoadingData}
                                                compact={false}
                                                styles={{root: {minWidth: 840}}}
                                                columns={listColumns}
                                                selectionMode={SelectionMode.none}
                                                layoutMode={DetailsListLayoutMode.justified}
                                                isHeaderVisible={isLoadedData}
                                                isSelectedOnFocus={false}
                                                setKey={"Id"}
                                            />
                                        </Stack>
                                    }


                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>


                                    <DefaultButton
                                        iconProps={{iconName: 'Upload'}}
                                        title={t('title.uploadCSV', {ns: 'approval'})}
                                        text={t('title.uploadCSV', {ns: 'approval'})}
                                        disabled={!isLoadedData || isLoadingData}
                                        onClick={() => {
                                            setShowUpload(true)
                                        }}
                                    />


                                    <DefaultButton
                                        onClick={() => {
                                            onBeforeDismissEmailPreview()
                                        }}
                                        styles={{root: {marginRight: 8}}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>

            {showUpload &&
                <ApprovalListUpload
                    item={list}
                    documentOnlyAudienceId={documentOnlyAudienceId}
                    showModal={showUpload}
                    onDismissModal={OnHideUpload}
                />
            }

        </>
    )
}


